import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Wind from "components/icons/Wind";
import { BULK_INVOICE_ACTION, BULK_INVOICE_TYPE } from "constants/invoicing";
import { useBillBulkAction } from "hooks/useBillBulkAction";
import { useToast } from "hooks/useToast";
import { RowSelectionState } from "react-table-8.10.7";
import { useBulkUpdateInvoiceMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { BillTable } from "./BillTable";

export default function BillDeleteModal({
  groupId,
  entityId,
  selectedRows,
  rowSelection,
  success,
}: {
  groupId: string;
  entityId: string;
  selectedRows: string[];
  rowSelection?: RowSelectionState;
  success: () => void;
}) {
  const [bulkUpdateOrDeleteBill, { isLoading: isDeletingBills }] =
    useBulkUpdateInvoiceMutation();
  const { alertToast, successToast } = useToast();

  const {
    allBills,
    isLoading,
    rowSelectionState,
    setRowSelectionState,
    newSelectedRows,
    valid,
    invalid,
    totalBills,
    isAllBillInvalid,
    shouldShowButton,
  } = useBillBulkAction({
    groupId,
    entityId,
    selectedRows,
    rowSelection,
    actionType: "delete",
  });

  const onDelete = async (rowIds?: string[]) => {
    try {
      if (!rowIds || rowIds.length < 1) return;
      await bulkUpdateOrDeleteBill({
        groupId: groupId!,
        entityId: entityId!,
        record_ids: rowIds,
        action: BULK_INVOICE_ACTION.DELETE,
        type: BULK_INVOICE_TYPE.BILL,
      }).unwrap();
      success();
      successToast({
        message: `Selected bills deleted.`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title>Delete bills</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <Async.Root
          isLoading={isLoading}
          isSuccess={!isLoading}
          isEmpty={false}
        >
          <Async.Success>
            {newSelectedRows.length > 0 && (
              <>
                {isAllBillInvalid && (
                  <div className="t-flex t-items-center t-gap-2 t-flex-col t-pb-12">
                    <Wind className="t-text-neutral-40" />
                    The{" "}
                    {pluralize(
                      newSelectedRows.length,
                      "bill is",
                      "bills are"
                    )}{" "}
                    already active and hence cannot be deleted.
                  </div>
                )}

                {valid.length > 0 && (
                  <>
                    <div className="t-mb-4">
                      Are you sure you want to delete the{" "}
                      <b>
                        {valid.length} out of {totalBills} bills{" "}
                      </b>
                      ? This action cannot be undone.
                    </div>
                    {invalid.length > 0 && (
                      <div className="t-mb-4">
                        <Banner
                          type="warning"
                          description={
                            <span>
                              The remaining{" "}
                              {pluralize(
                                invalid.length,
                                "bill is",
                                "bills are"
                              )}{" "}
                              already active and hence will not be deleted.
                            </span>
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            <BillTable
              billsData={allBills?.bills || []}
              disabledRowIds={invalid.map((row) => row?.uuid)}
              showActionsColumn={false}
              rowSelection={rowSelectionState}
              setRowSelection={setRowSelectionState}
            />
          </Async.Success>
        </Async.Root>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button>Cancel</Button>
        </Modal.RawClose>
        {shouldShowButton && (
          <Button
            customType="danger"
            onClick={() => onDelete(valid.map((row) => row.uuid))}
            isLoading={isDeletingBills}
            disabled={isDeletingBills}
          >
            Delete
          </Button>
        )}
      </Modal.FooterButtonGroup>
    </>
  );
}
